import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Car, TrendingUp, DollarSign, Clock, ArrowUpRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, PieChart, Pie, Legend } from "recharts";

function nzd(n) {
  return new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 }).format(n || 0);
}

function KpiCard({ testId, label, value, sub, icon: Icon, tone = "blue" }) {
  const tones = {
    blue: "text-blue-600 bg-blue-50",
    emerald: "text-emerald-600 bg-emerald-50",
    amber: "text-amber-600 bg-amber-50",
    rose: "text-rose-600 bg-rose-50",
  };
  return (
    <div data-testid={testId} className="card-elevated p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, v] = await Promise.all([api.get("/dashboard/stats"), api.get("/vehicles")]);
      setStats(s.data);
      setVehicles(v.data);
    })();
  }, []);

  if (!stats) return <div className="text-slate-500">Loading…</div>;

  const agingData = [
    { name: "Fresh 0-30d", value: stats.aging.fresh, fill: "#059669" },
    { name: "31-60d", value: stats.aging.moderate, fill: "#2563EB" },
    { name: "61-90d", value: stats.aging.aging, fill: "#D97706" },
    { name: "90d+", value: stats.aging.critical, fill: "#E11D48" },
  ];
  const pipeData = [
    { name: "Lead", value: stats.pipeline.lead },
    { name: "Test Drive", value: stats.pipeline.test_drive },
    { name: "Negotiating", value: stats.pipeline.negotiating },
    { name: "Deposit", value: stats.pipeline.deposit },
    { name: "Sold", value: stats.pipeline.sold },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Overview of stock, sales pipeline and margin performance.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-total-stock" label="Vehicles in Stock" value={stats.total_stock} sub={nzd(stats.total_stock_value) + " valuation"} icon={Car} tone="blue" />
        <KpiCard testId="kpi-sold-this-month" label="Sold This Month" value={stats.sold_this_month} sub={stats.total_customers + " total customers"} icon={TrendingUp} tone="emerald" />
        <KpiCard testId="kpi-gross-margin" label="Gross Profit (MTD)" value={nzd(stats.gross_profit_month)} sub="Sold vehicles this month" icon={DollarSign} tone="emerald" />
        <KpiCard testId="kpi-avg-stock-age" label="Avg Stock Age" value={`${stats.avg_stock_age}d`} sub={`${stats.aging.critical} vehicles over 90d`} icon={Clock} tone={stats.aging.critical > 0 ? "rose" : "amber"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 card-elevated p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Deal Pipeline</h3>
            <Link to="/deals" className="text-xs text-blue-600 font-semibold flex items-center gap-1">Open board <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pipeData}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {pipeData.map((_, i) => <Cell key={i} fill={["#94A3B8", "#2563EB", "#D97706", "#7C3AED", "#059669"][i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-4 card-elevated p-6">
          <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Stock Aging</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={agingData} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value" paddingAngle={2}>
                {agingData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-elevated p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Recent Inventory</h3>
          <Link to="/vehicles" className="text-xs text-blue-600 font-semibold flex items-center gap-1">View all <ArrowUpRight className="w-3 h-3" /></Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2.5 pl-2">Stock #</th>
                <th className="text-left">Vehicle</th>
                <th className="text-left">Rego</th>
                <th className="text-right">Cost</th>
                <th className="text-right">Price</th>
                <th className="text-right">Margin</th>
                <th className="text-left pl-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.slice(0, 5).map((v) => {
                const cost = (v.purchase_cost || 0) + (v.reconditioning_cost || 0) + (v.transport_cost || 0);
                const margin = (v.list_price || 0) - cost;
                return (
                  <tr key={v.id} className="border-b border-slate-100 row-hover">
                    <td className="py-3 pl-2 font-mono text-slate-700 text-xs">{v.stock_no}</td>
                    <td className="font-semibold text-slate-900">{v.year} {v.make} {v.model}</td>
                    <td className="font-mono text-xs">{v.rego}</td>
                    <td className="text-right font-mono text-xs">{nzd(cost)}</td>
                    <td className="text-right font-mono text-xs">{nzd(v.list_price)}</td>
                    <td className={`text-right font-mono text-xs font-semibold ${margin >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{nzd(margin)}</td>
                    <td className="pl-4"><span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded bg-slate-100 text-slate-700">{v.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
